import type { ReactNode } from "react";
import { Clock, Check, MapPin, Smartphone, Tablet } from "lucide-react";
import { useLocale } from "../i18n/useLocale";

/**
 * Device frames — a phone and a tablet bezel with a screen slot. Pass real app
 * screenshots as `children`; without them a neutral placeholder shows. Pick the
 * frame by feature (mobile flows → PhoneFrame, dashboards → TabletFrame).
 */
function ScreenPlaceholder({ kind }: { kind: "phone" | "tablet" }) {
  const { t } = useLocale();
  const Icon = kind === "phone" ? Smartphone : Tablet;
  return (
    <div className="grid h-full w-full place-items-center bg-[radial-gradient(120%_120%_at_50%_0%,var(--ds-tint),#ffffff_72%)]">
      <div className="flex flex-col items-center gap-2 text-ds-ink-soft/70">
        <Icon size={28} strokeWidth={1.6} />
        <span className="text-[11px] font-medium uppercase tracking-[0.12em]">{t("industries:mockup.appScreen")}</span>
      </div>
    </div>
  );
}

export function PhoneFrame({ children, className = "" }: { children?: ReactNode; className?: string }) {
  return (
    <div className={`relative mx-auto aspect-[9/19] w-full max-w-[270px] rounded-[2.6rem] bg-ds-ink p-2.5 shadow-[0_22px_50px_-22px_rgba(2,6,23,0.5)] ring-1 ring-white/10 ${className}`}>
      <div className="relative h-full w-full overflow-hidden rounded-[2.05rem] bg-bg-subtle">
        <div className="absolute left-1/2 top-3 z-10 h-5 w-[34%] -translate-x-1/2 rounded-full bg-ds-ink" />
        {children ?? <ScreenPlaceholder kind="phone" />}
      </div>
    </div>
  );
}

export function TabletFrame({ children, className = "" }: { children?: ReactNode; className?: string }) {
  return (
    <div className={`relative mx-auto aspect-[4/3] w-full max-w-[540px] rounded-[1.8rem] bg-ds-ink p-3 shadow-[0_40px_90px_-30px_rgba(2,6,23,0.7)] ring-1 ring-white/10 ${className}`}>
      <div className="relative h-full w-full overflow-hidden rounded-[1.15rem] bg-bg-subtle">
        {children ?? <ScreenPlaceholder kind="tablet" />}
      </div>
    </div>
  );
}

/**
 * Mockups — temporary "nice-enough" UI mockups used wherever a product mockup is
 * expected but the real screenshot isn't ready yet. Pure DS tokens, no images.
 *   PhoneMock — a phone running the Done-it check-in app (fits square/tall slots).
 *   BoardMock — a mini admin dashboard board (fits wide 16:9 / 4:3 slots).
 * Each fills its container and centres a scaled mock.
 */

const TINT = "bg-[radial-gradient(120%_120%_at_20%_0%,var(--ds-tint),#ffffff_70%)]";
const DOTS = { backgroundImage: "radial-gradient(var(--line-strong) 1px, transparent 1px)", backgroundSize: "18px 18px" } as const;

/**
 * PhoneMock — the app's actual home screen, rebuilt 1:1 from the client's own
 * screenshots (corrections web.pdf p.17 idle / p.18 running).
 *   running — green timer counting from the check-in time; Stop work red,
 *             Start break amber, everything else greyed out.
 *   idle    — "currently not active" with a dashed clock; Start work green and
 *             Start drive blue are the only live actions.
 * The timer + button group sits centred in the screen, as it does in the app.
 */
/**
 * PhoneShot — a photo of the app running on a phone, cropped to the handset.
 * Replaces the hand-built CheckInScreen where a real device shot is wanted.
 * NOTE: the source screenshots are Dutch only, so these read as Dutch on every
 * locale — unlike CheckInScreen, which was translated into all thirteen.
 */
const PHONE_SHOTS = {
  running: "https://res.cloudinary.com/lbx0aw1r/image/upload/c_crop,x_33,y_18,w_510,h_1045/f_auto,q_auto,w_420/v1784703909/Check_out_gmppzw.png",
  idle: "https://res.cloudinary.com/lbx0aw1r/image/upload/c_crop,x_79,y_26,w_510,h_1045/f_auto,q_auto,w_420/v1784703909/Check_it_dcqaoo.png",
} as const;

export function PhoneShot({ state = "running", className = "" }: { state?: "running" | "idle"; className?: string }) {
  return (
    <img
      src={PHONE_SHOTS[state]}
      alt=""
      width={420}
      height={816}
      loading="lazy"
      decoding="async"
      // The handset corner is a squircle, so a circular CSS radius matching the
      // bezel's 69px still leaves a wedge of the shot's grey backdrop on the
      // diagonal (measured: 74px needed). 79px — 15.5% of the 510px crop, 7.5%
      // of its 1045px — clears it, at the cost of a few px of black frame.
      style={{ borderRadius: "15.5% / 7.5%" }}
      className={`mx-auto h-full w-auto max-w-full object-contain ${className}`}
    />
  );
}

/**
 * CheckInScreen — just the app screen, no bezel, so it can fill either the
 * standalone `PhoneMock` or a bare `PhoneFrame` slot.
 */
export function CheckInScreen({ state = "running" }: { state?: "running" | "idle" }) {
  const { t } = useLocale();
  const running = state === "running";
  const buttons: [string, string][] = running
    ? [
        [t("industries:mockup.phone.startWork"), ""],
        [t("industries:mockup.phone.stopWork"), "bg-[#e0503a] text-white font-bold"],
        [t("industries:mockup.phone.startBreak"), "bg-[#f0a020] text-white font-bold"],
        [t("industries:mockup.phone.stopBreak"), ""],
        [t("industries:mockup.phone.startDrive"), ""],
        [t("industries:mockup.phone.stopDrive"), ""],
      ]
    : [
        [t("industries:mockup.phone.startWork"), "bg-[#4ba85f] text-white font-bold"],
        [t("industries:mockup.phone.stopWork"), ""],
        [t("industries:mockup.phone.startBreak"), ""],
        [t("industries:mockup.phone.stopBreak"), ""],
        [t("industries:mockup.phone.startDrive"), "bg-[#3d7fbf] text-white font-bold"],
        [t("industries:mockup.phone.stopDrive"), ""],
      ];
  const inactive = "bg-[#f1f2f4] text-ds-ink-soft/55 font-medium";
  return (
        <div className="flex h-full w-full flex-col overflow-hidden bg-white">
          {/* app bar */}
          <div className="flex items-center justify-between border-b border-line/70 px-3 py-2">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-white text-[11px] leading-none text-ds-accent ring-1 ring-line">≡</span>
            <span className="text-[11px] font-semibold text-ds-accent">{t("industries:mockup.phone.greeting")}</span>
            <span className="h-5 w-5" />
          </div>
          {/* timer + actions, centred in the screen as in the app */}
          <div className="flex flex-1 flex-col justify-center px-3 pb-4">
            <div className="grid place-items-center text-center">
              {running ? (
                <>
                  <p className="text-[9px] font-semibold text-ds-pos">{t("industries:mockup.phone.workingSince")}</p>
                  <p className="mt-1 font-display text-[22px] font-extrabold leading-none tracking-tight text-ds-pos">00:00:00</p>
                  <p className="mt-1 text-[8px] text-ds-ink-soft">{t("industries:mockup.phone.atSite")}</p>
                </>
              ) : (
                <>
                  <p className="text-[9px] font-semibold text-ds-ink-soft/60">{t("industries:mockup.phone.notActive")}</p>
                  <p className="mt-1 font-display text-[22px] font-extrabold leading-none tracking-tight text-ds-ink-soft/35">--:--</p>
                </>
              )}
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2">
              {buttons.map(([label, tone]) => (
                <span key={label} className={`grid min-h-[42px] place-items-center whitespace-nowrap rounded-xl px-1 text-center text-[9.5px] leading-tight ${tone || inactive}`}>{label}</span>
              ))}
            </div>
          </div>
        </div>
  );
}

/** The check-in screen in its own bezel on a tinted backdrop. */
export function PhoneMock({ state = "running" }: { state?: "running" | "idle" }) {
  return (
    <div className={`relative grid h-full w-full place-items-center overflow-hidden ${TINT} p-5`}>
      <div className="pointer-events-none absolute inset-0 opacity-40" style={DOTS} />
      <div className="relative aspect-[9/18.5] h-[92%] max-h-[380px] overflow-hidden rounded-[2rem] bg-ds-ink p-[6px] shadow-[0_30px_60px_-20px_rgba(15,23,42,0.5)]">
        <div className="h-full w-full overflow-hidden rounded-[1.6rem]">
          <CheckInScreen state={state} />
        </div>
      </div>
    </div>
  );
}

export function BoardMock() {
  const { t } = useLocale();
  const bars = [40, 56, 48, 70, 60, 82, 72];
  const stats = [
    { l: t("industries:mockup.board.active"), v: "14" },
    { l: t("industries:mockup.board.today"), v: "9" },
    { l: t("industries:mockup.board.hours"), v: "328h" },
  ];
  return (
    <div className={`relative grid h-full w-full place-items-center overflow-hidden ${TINT} p-5`}>
      <div className="pointer-events-none absolute inset-0 opacity-40" style={DOTS} />
      <div className="relative w-[92%] max-w-[440px] rounded-2xl bg-white p-4 shadow-[0_28px_60px_-20px_rgba(15,23,42,0.35)] ring-1 ring-black/[0.04]">
        {/* topbar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-6 w-6 place-items-center rounded-lg bg-ds-accent text-white"><Clock size={13} strokeWidth={2.2} /></span>
            <span className="text-[12px] font-bold text-ds-ink">{t("industries:mockup.board.dashboard")}</span>
          </div>
          <span className="grid h-6 w-6 place-items-center rounded-full bg-ds-gold text-[9px] font-bold text-ds-ink">AE</span>
        </div>
        {/* stat tiles */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          {stats.map((s) => (
            <div key={s.l} className="rounded-lg bg-bg-subtle p-2">
              <div className="text-[8px] text-ds-ink-soft">{s.l}</div>
              <div className="mt-0.5 text-[15px] font-bold leading-none text-ds-ink">{s.v}</div>
            </div>
          ))}
        </div>
        {/* chart */}
        <div className="mt-2 rounded-xl bg-bg-subtle p-3">
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-semibold text-ds-ink-soft">{t("industries:mockup.board.hoursThisWeek")}</span>
            <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-ds-pos"><span className="h-1.5 w-1.5 rounded-full bg-ds-pos" /> +8%</span>
          </div>
          <div className="mt-2 flex h-12 items-end gap-1.5">
            {bars.map((h, i) => (
              <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: i === bars.length - 1 ? "var(--ds-accent)" : "var(--ds-accent-soft, rgba(98,171,222,0.35))" }} />
            ))}
          </div>
        </div>
        {/* rows */}
        <div className="mt-2 space-y-1.5">
          {[
            { n: "Albert Einstein", p: "SGD BCN" },
            { n: "Julia Roberts", p: "Office fit-out" },
          ].map((r) => (
            <div key={r.n} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-1.5">
              <span className="grid h-5 w-5 place-items-center rounded-full bg-ds-accent/15 text-ds-accent"><MapPin size={10} /></span>
              <span className="flex-1 text-[10px] font-medium text-ds-ink">{r.n}</span>
              <span className="text-[9px] text-ds-ink-soft">{r.p}</span>
              <span className="grid h-4 w-4 place-items-center rounded-full bg-ds-pos text-white"><Check size={9} strokeWidth={3.5} /></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
